import { motion } from 'framer-motion';
import { useRef } from 'react';

import IconInCircle from '../../../../../components/IconInCircle/IconInCircle';
import Stack from '../../../../../components/Stack/Stack';
import useIsMobile from '../../../../../hooks/ui/useIsMobile';
import useMeasure from '../../../../../hooks/useMeasure';
import BookingParameters from '../../../../types/BookingParameters';
import ComposeBookingStep from '../../../../types/ComposeBookingStep';
import compileStepUrl from '../../helpers/compileStepUrl';
import ComposeBookingStepsUrls from '../../types/ComposeBookingStepsUrls';
import BookingParametersBar from '../BookingParametersBar/BookingParametersBar';
import BookingParametersHero from '../BookingParametersHero/BookingParametersHero';
import StepsNavigation from '../StepsNavigation/StepsNavigation';
import TintOnHoverLinkButton from '../TintOnHoverButton/TintOnHoverLinkButton';

import arrowIcon from './graphics/arrow.svg?raw';

import styles from './BookingParametersHeader.module.css';

const determinePreviousStep = (
    currentStep: ComposeBookingStep
): ComposeBookingStep | undefined => {
    switch (currentStep) {
        case 'pickupDate':
        case 'passengers':
        case 'numberOfBoats':
        case 'expired':
            return 'boatModel';

        case 'boatModel':
            return 'dock';
    }

    return undefined;
};

type BookingParametersHeaderProps = {
    parameters: BookingParameters;
    stepUrls: ComposeBookingStepsUrls;
    currentStep: ComposeBookingStep;
    dimmed?: boolean;
};

const BookingParametersHeader = ({
    parameters,
    currentStep,
    stepUrls,
    dimmed,
}: BookingParametersHeaderProps) => {
    const isMobile = useIsMobile();
    const heroRef = useRef<HTMLDivElement>(null);
    const measurements = useMeasure(heroRef);

    const previousStep = determinePreviousStep(currentStep);
    const heroUrl = previousStep
        ? compileStepUrl(previousStep, stepUrls, parameters)
        : undefined;

    const askForPassengers = !!parameters.boatModel?.askNumberOfPassengers;

    const heroImage = (
        <BookingParametersHero
            borderRadiusSize="large"
            parameters={parameters}
            onlyTitle={!isMobile}
            editMode
        />
    );

    return (
        <Stack space="none">
            <motion.div
                initial={{ height: 0 }}
                animate={{ height: measurements.height }}
                exit={{ height: 0 }}
                transition={{ duration: 0.2 }}
            >
                <div ref={heroRef} className={styles.Hero}>
                    {heroUrl ? (
                        <TintOnHoverLinkButton
                            href={heroUrl}
                            borderRadius="top"
                            borderRadiusSize="large"
                            fullHeight
                            fullWidth
                        >
                            {heroImage}
                            <IconInCircle
                                src={arrowIcon}
                                size="xlarge"
                                variant="black-blended"
                                className={styles.BackIcon}
                            />
                        </TintOnHoverLinkButton>
                    ) : (
                        heroImage
                    )}
                </div>
            </motion.div>

            {isMobile ? (
                <StepsNavigation
                    currentStep={currentStep}
                    stepUrls={stepUrls}
                    parameters={parameters}
                    noPassengers={!askForPassengers}
                />
            ) : (
                <BookingParametersBar
                    dimmed={dimmed}
                    stepUrls={stepUrls}
                    parameters={parameters}
                    noPassengers={!askForPassengers}
                />
            )}
        </Stack>
    );
};

export default BookingParametersHeader;
